/**
 * src/lib/voice/useHoldToTalk.js — TICKET-097
 *
 * Press-and-hold gesture detector for the voice orb. Pure UI timing logic —
 * the caller supplies what "hold" means (hard-stop TTS + record) via
 * onHoldStart/onHoldEnd; this hook only decides WHEN a press becomes a hold.
 *
 * Contract:
 *   - pointerdown starts a timer; if the pointer is still down after
 *     HOLD_DELAY_MS the hold begins (onHoldStart fires once).
 *   - pointerup / pointercancel / pointerleave end the hold (onHoldEnd) —
 *     a cancel mid-hold still PROCESSES the recorded audio rather than
 *     discarding the CEO's words.
 *   - a release before the delay is a plain tap: neither callback fires and
 *     the button's normal onClick proceeds.
 *   - after a completed hold, the browser still synthesizes a click on
 *     release; the consumer swallows it via consumeHoldClick().
 *
 * iOS notes (memory: mobile touch events): pointer events are used instead
 * of click/touch pairs to avoid double-firing; consumers should also set
 * touchAction:'none' + WebkitTouchCallout:'none' on the element so long-press
 * doesn't trigger the magnifier/context menu.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export const HOLD_DELAY_MS = 250;

export function useHoldToTalk({ onHoldStart, onHoldEnd, holdDelayMs = HOLD_DELAY_MS } = {}) {
  const [isHolding, setIsHolding] = useState(false);
  const timerRef = useRef(null);
  const holdingRef = useRef(false);
  const suppressClickRef = useRef(false);
  // Ref-captured callbacks so handlers stay referentially stable across
  // parent re-renders (same pattern as onConversationPersistedRef).
  const cbRef = useRef({ onHoldStart, onHoldEnd });
  cbRef.current = { onHoldStart, onHoldEnd };

  const clearTimer = () => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
  };

  const onPointerDown = useCallback((e) => {
    if (holdingRef.current || timerRef.current) return; // second finger / repeat — ignore
    e?.preventDefault?.();
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      holdingRef.current = true;
      setIsHolding(true);
      cbRef.current.onHoldStart?.();
    }, holdDelayMs);
  }, [holdDelayMs]);

  const release = useCallback(() => {
    clearTimer();
    if (!holdingRef.current) return; // released before the delay → plain tap
    holdingRef.current = false;
    setIsHolding(false);
    suppressClickRef.current = true;
    cbRef.current.onHoldEnd?.();
  }, []);

  // Unmount mid-press: drop the timer; never fire callbacks into a dead tree.
  useEffect(() => () => { clearTimer(); holdingRef.current = false; }, []);

  /** Swallow the synthetic click that follows a completed hold (one-shot). */
  const consumeHoldClick = useCallback(() => {
    const v = suppressClickRef.current;
    suppressClickRef.current = false;
    return v;
  }, []);

  return {
    isHolding,
    consumeHoldClick,
    handlers: {
      onPointerDown,
      onPointerUp: release,
      onPointerCancel: release,
      onPointerLeave: release,
    },
  };
}

export default useHoldToTalk;
